<template>
	<component :is="tag" class="b-btn" :class="classes" v-on="$listeners">
		<slot></slot>
	</component>
</template>

<script>
export default {
	name: "b-btn",
	props: {
		tag: {
			type: String,
			default: "button",
		},
		variant: {
			type: String,
			default: "contained",
			validator: (value) => ["contained", "outline", "text"].includes(value),
		},
		color: {
			type: String,
			default: "primary",
			validator: (value) => ["primary", "secondary"].includes(value),
		},
		size: {
			type: String,
			default: "m",
			validator: (value) => ["s", "m", "l", "s/m"].includes(value),
		},
		shape: {
			type: String,
			default: "default",
			validator: (value) => ["circle", "default", "sqare"].includes(value),
		},
	},
	computed: {
		classes() {
			return [
				`b-btn--${this.variant}-${this.color}`,
				`b-btn--${this.size}`,
				`b-btn--${this.shape}`,
			];
		},
	},
};
</script>

<style lang="scss">
.b-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 10px;

	background-color: transparent;
	height: var(--height);
	border: 1px solid transparent;
	flex-shrink: 0;

	//size
	&--l {
		--height: var(--control-height-l);
		padding-inline: 40px;
		font-size: var(--font-size-m);
	}

	&--m {
		--height: var(--control-height-m);
		padding-inline: 16px;
		font-size: var(--font-size-m);
	}

	&--s {
		--height: var(--control-height-s);
	}

	&--s\/m {
		--height: var(--control-height-s-m);
	}

	// shape
	&--default {
		border-radius: 50px;
	}

	&--circle {
		width: var(--height);
		border-radius: 50%;
		padding: 0;
	}

	&--square {
		width: var(--height);
		border-radius: 4px;
		padding: 0;
	}

	// color & variant
	&--contained-primary {
		background-color: var(--color-primary-600);
		color: #fff;

		&:not(:disabled) {
			&:is(:hover, :focus) {
				background-color: var(--color-primary-700);
			}

			&:active {
				background-color: var(--color-primary-800);
			}
		}

		&:disabled {
			background-color: var(--color-neutral-175);
			cursor: not-allowed;
		}
	}

	&--contained-secondary {
		background-color: var(--color-neutral-0);
		color: var(--color-neutral-900);

		&:not(:disabled) {
			&:is(:hover, :focus) {
				background-color: var(--color-neutral-25);
			}

			&:active {
				background-color: var(--color-neutral-100);
			}
		}

		&:disabled {
			background-color: var(--color-neutral-175);
			cursor: not-allowed;
		}
	}

	&--outline-primary {
		--color: var(--color-primary-600);
		border-color: var(--color);
		color: var(--color);

		&:not(:disabled) {
			&:is(:hover, :focus) {
				--color: var(--color-primary-700);
			}

			&:active {
				--color: var(--color-primary-800);
			}
		}

		&:disabled {
			--color: var(--color-primary-175);
			cursor: not-allowed;
		}
	}

	&--text-primary {
		color: var(--color-primary-600);

		&:not(:disabled) {
			&:hover {
				color: var(--color-primary-700);
			}

			&:focus {
				color: var(--color-primary-700);
				background-color: var(--color-neutral-75);
			}

			&:active {
				color: var(--color-primary-800);
				background-color: var(--color-neutral-75);
			}
		}

		&:disabled {
			color: var(--color-neutral-175);
			cursor: not-allowed;
		}
	}
}
</style>
